import { Injectable } from '@angular/core';
import { Apollo, Query } from 'apollo-angular';
import gql from 'graphql-tag';

import { Group, Location, LocationInput, PagedResult, ServiceProviderConnectionInput } from '@core/interfaces';

@Injectable({
  providedIn: 'root'
})
export class LocationService {

  constructor(private apollo: Apollo) {}

  public async getLocationById(id: string): Promise<Location> {
    return this.apollo
      .query<Location>({
        query: gql`
          query QueryLocationGQL($id:ID!){
            location(id: $id) {
              id
              name
              documentNumber
              group {
                id
                name
                archived
              }
              segments {
                id
                name
                archived
              }
              archived
              notes
              address {
                postalCode
                streetName
                city
                number
                complement
                neighborhood
                state
                coords {
                  latitude
                  longitude
                }
                formattedAddress
              }
              contact {
                emails {
                  name
                  contactType
                  emailAddress
                  primary
                }
                phones {
                  name
                  contactType
                  number
                  primary
                }
              }
            }
          }
        `,
        variables: {
          id: id
        }
      }).toPromise()
      .then((result) => result.data['location']);
  }

  public async loadData({ where, options }): Promise<PagedResult<Location>> {
    return this.apollo
      .query({
        query: gql`
          query locations($where: LocationWhereInput $limit: Int $offset: Int $orderBy: LocationOrderByObjectInput) {
            locations: locationsNew(
              where: $where
              limit: $limit
              offset: $offset
              orderBy: $orderBy
            ){
              totalCount
              items {
                id
                name
                archived
                address {
                  streetName
                  neighborhood
                  city
                  number
                  postalCode
                  coords {
                    latitude
                    longitude
                  }
                  formattedAddress
                }
                segments {
                  id
                }
              }
            }
          }`,
        variables: {
          where,
          orderBy: options.orderBy,
          limit: options.limit,
          offset: options.offset,
        }
      })
      .toPromise()
      .then((response) => response.data['locations'] as PagedResult<Location>)
      .then((page) => {
        return {
          items: page.items,
          totalCount: page.totalCount,
          filtered: Boolean(
            where.name_contains ||
            where.archived !== false ||
            (where.companyId_in && where.companyId_in.length) ||
            where.documentNumber_contains
          )
        };
      });
  }

  public async getLocationServiceProviders(
    { locationId, limit, offset, orderBy }: ServiceProviderConnectionInput): Promise<Location> {
    return this.apollo
      .query({
        query: gql`
          query getLocationServiceProviders($locationId: ID!, $limit: Int!, $offset:Int!, $orderBy: ServiceProviderOrderByEnum) {
            location(id: $locationId) {
              id
              name
              serviceProvidersConnection(limit: $limit, offset: $offset, orderBy: $orderBy){
                pageInfo {
                  hasNextPage
                  hasPreviousPage
                }
                totalCount
                items {
                  company {
                    id
                    name
                    archived
                  }
                  segment {
                    id
                    name
                    archived
                  }
                  maintenancesTypes {
                    id
                    name
                  }
                  deadlineContract {
                    id
                    name
                  }
                  createdAt
                }
              }
            }
          }`,
          variables: {
            locationId,
            limit,
            offset,
            orderBy
          }
      })
      .toPromise()
      .then((response) => {
        return response.data['location'] as Location;
      });
  }

  public async create(location: LocationInput): Promise<Location> {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation createLocation($input: LocationInput!) {
            createLocation(input: $input) {
              id
              name
              documentNumber
              group {
                id
                name
                archived
              }
              serviceProviders(limit: 1000) {
                company {
                  id
                  name
                  archived
                }
                segment {
                  id
                  name
                  archived
                }
                maintenancesTypes {
                  id
                  name
                }
              }
              segments {
                id
                name
                archived
              }
              archived
              notes
              address {
                postalCode
                streetName
                city
                number
                complement
                neighborhood
                state
                coords {
                  latitude
                  longitude
                }
                formattedAddress
              }
              contact {
                emails {
                  name
                  contactType
                  emailAddress
                  primary
                }
                phones {
                  name
                  contactType
                  number
                  primary
                }
              }
            }
          }
        `,
        variables: { input: location }
      })
      .toPromise()
      .then((result) => result.data['createLocation']);
  }

  public async update(id: string, location: LocationInput): Promise<Location> {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation updateLocation($id: ID!, $input: LocationInput!) {
            updateLocation(id: $id, input: $input) {
              id
              name
              documentNumber
              group {
                id
                name
                archived
              }
              serviceProviders(limit: 1000) {
                company {
                  id
                  name
                  archived
                }
                segment {
                  id
                  name
                  archived
                }
                maintenancesTypes {
                  id
                  name
                }
              }
              segments {
                id
                name
                archived
              }
              archived
              notes
              address {
                postalCode
                streetName
                city
                number
                complement
                neighborhood
                state
                coords {
                  latitude
                  longitude
                }
                formattedAddress
              }
              contact {
                emails {
                  name
                  contactType
                  emailAddress
                  primary
                }
                phones {
                  name
                  contactType
                  number
                  primary
                }
              }
            }
          }
        `,
        variables: {
          id: id,
          input: location
        }
      })
      .toPromise()
      .then((result) => result.data['updateLocation']);
  }

  public async save(locationId: string, locationInput: LocationInput): Promise<Location> {
    return locationId
      ? this.update(locationId, locationInput)
      : this.create(locationInput);
  }

  public async archiveLocation(id: string): Promise<Location> {
    return this.apollo.mutate({
      mutation: gql`
      mutation archiveLocation($id: ID!) {
        archiveLocation(id: $id) {
          id
          name
          archived
        }
      }
    `,
      variables: {
        id: id
      }
    })
      .toPromise()
      .then((response) => response.data['archiveLocation']);
  }

  public async unarchiveLocation(id: string): Promise<Location> {
    return this.apollo.mutate({
      mutation: gql`
      mutation unarchiveLocation($id: ID!) {
        unarchiveLocation(id: $id) {
          id
          name
          archived
        }
      }
    `,
      variables: {
        id: id
      }
    })
      .toPromise()
      .then((response) => response.data['unarchiveLocation']);
  }

  public async listGroups({ where, locationId, limit, offset }: { where: any, locationId?: string, limit: number, offset: number }): Promise<PagedResult<Group>> {
    return this.apollo
      .query({
        query: gql`
          query listGroups($where: GroupWhereInput, $locationId: ID $limit: Int, $offset:Int) {
            groups(where: $where, orderBy: name_asc, limit: $limit, offset: $offset) {
              totalCount
              items {
                id
                name
                type
                archived
                description
                locked
                locationsConnection(where: { id: $locationId }, limit: 1, offset: 0){
                  items {
                    id
                  }
                }
              }
            }
          }`,
        variables: {
          where,
          locationId,
          limit,
          offset
        }
      })
      .toPromise()
      .then((response) => response.data['groups'] as PagedResult<Group>);
  }

  public async addLocationToGroup({ locationId, groupIds }: { locationId: string, groupIds: string[], }): Promise<Location[]> {
    return this.apollo.mutate({
      mutation: gql`
        mutation addLocationToGroup($input: AddLocationToGroupInput! $locationId: ID!) {
          addLocationToGroup(input: $input) {
            location {
              id
            }
            group {
              id
              locationsConnection(where: { id: $locationId }, limit: 1, offset: 0){
                items {
                  id
                }
              }
            }
          }
        }
      `,
      variables: {
        locationId: locationId,
        input: {
          items: groupIds.map((groupId) => ({
            locationId: locationId,
            groupId: groupId
          }))
        }
      }
    }).toPromise()
      .then((response: any) => response.data.addLocationToGroup.map((item: { location: Location }) => item && item.location));
  }

  public async removeLocationFromGroup(items: Array<{ locationId: string, groupId: string }>): Promise<void> {
    await this.apollo.mutate({
      mutation: gql`
        mutation removeLocationFromGroup($input: RemoveLocationFromGroupInput!) {
          removeLocationFromGroup(input: $input) {
            group {
              id
            }
            location {
              id
            }
          }
        }
      `,
      variables: {
        input: {
          items
        }
      }
    }).toPromise();
  }
}

@Injectable({
  providedIn: 'root'
})
export class QueryAddressGeocodeGQL extends Query<any> {
  document = gql`
    query QueryAddressGeocodeGQL ($input: AddressGeocodeInput!) {
      addressGeocode (input: $input) {
        postalCode
        streetName
        city
        number
        state
        complement
        neighborhood
        formattedAddress
        coords {
          latitude
          longitude
        }
      }
    }
  `;
}

@Injectable({
  providedIn: 'root'
})
export class QueryCoordinatesGeocodeGQL extends Query<any> {
  document = gql`
    query QueryCoordinatesGeocodeGQL ($input: CoordinatesGeocodeInput!) {
      coordinatesGeocode (input: $input) {
        country
        postalCode
        streetName
        city
        number
        state
        complement
        neighborhood
        formattedAddress
        coords {
          latitude
          longitude
        }
      }
    }
  `;
}

@Injectable({
  providedIn: 'root'
})
export class QueryAddressPostalCodeGQL extends Query<any> {
  document = gql`
    query QueryAddressPostalCodeGQL ($postalCode: String!) {
      addressPostalCode (postalCode: $postalCode) {
        postalCode
        streetName
        city
        number
        complement
        neighborhood
        state
        formattedAddress
      }
    }
  `;
}

@Injectable({
  providedIn: 'root'
})
export class QueryAddressCoordinatesGQL extends Query<any> {
  document = gql`
    query QueryAddressCoordinatesGQL ($address: String!) {
      addressCoordinates (address: $address) {
        latitude
        longitude
      }
    }
  `;
}
